import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { ZepApiError, ZepApiStatusType } from '@zep/apis';
import { PlanStatus } from '@zep/types';
import { usePlanPaymentInfo, useRemovePaymentCard, useSellingSpacePlanTemplates } from '../../../../services';
import { SubscribePlan } from '../../../../types';
import { PlanTemplateType } from '../../plan-payment';
import { useGetPlanInfo } from './hooks/useGetPlanInfo';
import { SubscriptionForeignPayment } from './SubscriptionForeignPayment';
import { SubscriptionKrPayment } from './SubscriptionKrPayment';
import { SubscriptionPaymentProviderProps } from './SubscriptionPaymentProvider';
const subscribePlans: SubscribePlan[] = ['pro', 'basic'];
export const SubscriptionPaymentSection = () => {
  const {
    query
  } = useRouter();
  const spaceHashId = (query.spaceHashId || '').toString();
  const subscriptionRequestHashId = (query.subscriptionRequestHashId || '').toString();
  const queryPlan = (query.plan || '').toString().toLowerCase() as SubscribePlan;
  const plan = subscribePlans.includes(queryPlan) ? queryPlan : 'basic';
  const {
    mutateAsync: removePaymentCard
  } = useRemovePaymentCard();
  const {
    // @ts-ignore
    data: {
      spacePlan,
      space,
      billingInfo,
      subscription
    }
  } = usePlanPaymentInfo(spaceHashId, {
    suspense: true
  });
  const {
    data: spacePlanProducts
  } = useSellingSpacePlanTemplates({
    spaceHashId,
    templateType: PlanTemplateType.PRODUCT_SUBSCRIPTION,
    subscriptionRequestHashId: subscriptionRequestHashId || undefined
  }, {
    enabled: true
  });
  const isKR = space.countryCode === 'KR';
  const getPlan = useGetPlanInfo({
    currency: isKR ? 'KRW' : 'USD',
    isAnnuallyPayment: query.subscriptionPeriod === '365',
    customSubscriptionRequestPriceInfo: spacePlanProducts.customSubscriptionRequestPriceInfo
  });
  const {
    maxCcu,
    plan: findPlan,
    ...rest
  } = getPlan({
    plans: spacePlanProducts.planProducts,
    planType: plan,
    maxCcu: Number(query.maxCcu)
  });
  const initMaxCcu = maxCcu ?? Object.values(spacePlanProducts.planProducts).flatMap(d => d)[0].maxCcu;
  const providerProps: SubscriptionPaymentProviderProps = {
    plan: findPlan,
    maxCcu: initMaxCcu,
    spaceHashId: String(query.spaceHashId),
    isKR,
    spacePlan,
    planInfo: {
      ...rest,
      maxCcu
    },
    space,
    spacePlanProducts: spacePlanProducts.planProducts,
    showEnterpriseSample: spacePlanProducts.showEnterpriseSample,
    billingInfo,
    subscription,
    isUpdated: spacePlan?.status === PlanStatus.ACTIVE,
    subscriptionRequestHashId: subscriptionRequestHashId || undefined,
    customSubscriptionRequestPriceInfo: spacePlanProducts.customSubscriptionRequestPriceInfo
  };
  useEffect(() => {
    // 실제 플랜이 없고 스페이스 빌링키만 잇을 경우 beforeunload 시점에서 삭제해서 실수 할 가능성을 줄이는 코드
    const removeCard = () => {
      if (!spacePlan && billingInfo) {
        removePaymentCard(spaceHashId);
      }
    };
    window.addEventListener('beforeunload', removeCard);
    return () => {
      window.removeEventListener('beforeunload', removeCard);
    };
  }, [removePaymentCard, billingInfo, spaceHashId, spacePlan]);
  useEffect(() => {
    if (spacePlan?.paymentFailCount > 0) {
      throw new ZepApiError(ZepApiStatusType.FAILED, 'payment_status_fail', 'payment_status_fail', 'payment_status_fail');
    }
  }, [spacePlan?.paymentFailCount]);
  return <>
      {isKR ? <SubscriptionKrPayment {...providerProps} /> : <SubscriptionForeignPayment {...providerProps} />}
    </>;
};